import { ThemeClassNames, withTheme } from "@/withTheme";
import { AnimatePresence, motion } from "framer-motion";
import { ComponentProps, useEffect } from "react";
import { createPortal } from "react-dom";
import { usePause } from "../hooks/common/usePause";
import { twMerge } from "tailwind-merge";

export interface ToastProps {
  id: number;
  duplicated?: boolean;
  message?: string;
  visible?: boolean;
  onClose?: (id: number) => void;
  duration?: number;
}
export const Toast = ({
  message,
  visible,
  onClose,
  duration = 3000,
  duplicated,
  id,
}: ToastProps) => {
  const { controls, togglePause, remainingTime, isPaused } = usePause(
    {
      transform: "scaleX(0)",
    },
    duration
  );
  useEffect(() => {
    const sti = setTimeout(() => onClose?.(id), remainingTime);
    if (isPaused) clearTimeout(sti);
    return () => {
      if (sti === undefined) return;
      clearTimeout(sti);
    };
  }, [onClose, id, remainingTime, isPaused]);
  return createPortal(
    <AnimatePresence>
      {visible && (
        <motion.div
          data-touch
          layout
          onClick={togglePause}
          initial={{
            opacity: 0,
            transform: "translateY(100%)",
          }}
          animate={{
            opacity: 1,
            transform: "translateY(0)",
          }}
          exit={{
            opacity: 0,
            transform: "translateX(100%)",
          }}
          className={twMerge(
            "rounded-md p-3 py-6 relative bg-white border w-full truncate shadow-md last-of-type:mb-0 text-xl",
            duplicated && "-mb-20"
          )}
        >
          {message}
          <motion.i
            initial={{
              transform: "scaleX(1)",
            }}
            animate={controls}
            className={twMerge(
              "h-1 absolute bottom-0 left-0 w-full transition-colors origin-left",
              isPaused ? "bg-gray-600" : "bg-yellow"
            )}
          />
        </motion.div>
      )}
    </AnimatePresence>,
    document.getElementById("toast")!
  );
};
const className: ThemeClassNames = {
  primary: "bg-yellow text-white",
  secondary: "bg-[#E9E9E9]",
  md: "h-10 text-xs font-medium rounded",
  xl: "h-14 text-base",
};
export type ThemedToastProps = ComponentProps<typeof ThemedToast>;
export const ThemedToast = withTheme(Toast, className);
